import { getBackwardStatusChange, statusMenuActionLabel } from "@/components/VideoProjectStatusPicker";
import { VideoProject } from "@/services/bedrock";

import { isQaReviewStatus } from "./qaReview";
import { editorSubmitActionLabel, editorSubmitEvent, isEditorResubmit } from "./submitEvent";

type EditorPageActionIntent =
  | { type: "status_event"; event: VideoProject.Event; userId?: number }
  | { type: "submit"; event: VideoProject.Event.SUBMIT_FOR_QA_REVIEW | VideoProject.Event.COMPLETE }
  | { type: "save" }
  | { type: "cancel" }
  | { type: "qa_review" }
  | { type: "view_qa" }
  | { type: "reopen" }
  | { type: "duplicate" }
  | { type: "archive" };

export interface EditorPageActionSpec {
  text: string;
  icon?: string;
  intent: EditorPageActionIntent;
}

interface EditorPageActionMapping {
  primary?: EditorPageActionSpec;
  secondary?: EditorPageActionSpec;
  menu: EditorPageActionSpec[];
}

const VIEW_QA_STUDIO_ACTION: EditorPageActionSpec = {
  text: "View QA studio",
  intent: { type: "view_qa" },
};

function withViewQaStudio(
  mapping: EditorPageActionMapping,
  options: { status: VideoProject.Status; archived?: boolean | null }
): EditorPageActionMapping {
  const continueQaReview =
    mapping.primary?.intent.type === "qa_review" &&
    options.status === VideoProject.Status.QA_REVIEW &&
    !options.archived;
  if (continueQaReview) return mapping;
  return { ...mapping, menu: [VIEW_QA_STUDIO_ACTION, ...mapping.menu] };
}

export function getEditorPageActions(options: {
  status: VideoProject.Status;
  archived?: boolean | null;
  requiresQa?: boolean | null;
  qaStatus?: VideoProject["qaStatus"];
  revisionCount?: number | null;
  canManageVideoProjectQueue?: boolean;
  hasPost?: boolean;
  currentUserId?: number;
  hasUnsavedChanges?: boolean;
}): EditorPageActionMapping {
  const {
    status,
    archived,
    requiresQa,
    qaStatus,
    revisionCount,
    canManageVideoProjectQueue,
    hasPost,
    currentUserId,
    hasUnsavedChanges,
  } = options;
  let primary: EditorPageActionSpec | undefined;
  let secondary: EditorPageActionSpec | undefined;

  if (archived) {
    return withViewQaStudio(
      {
        primary: { text: "Duplicate", icon: "copy", intent: { type: "duplicate" } },
        menu: [],
      },
      { status, archived }
    );
  }

  if (canManageVideoProjectQueue && isQaReviewStatus(status)) {
    primary = {
      text: status === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW ? "Start QA review" : "Continue QA review",
      intent: { type: "qa_review" },
    };
  } else if (status === VideoProject.Status.SUBMITTED_FOR_EDITING) {
    primary = {
      text: "Start editing",
      intent: { type: "status_event", event: VideoProject.Event.START_EDITING, userId: currentUserId },
    };
  } else if (status === VideoProject.Status.EDITING && hasPost) {
    if (hasUnsavedChanges) {
      primary = { text: "Save", intent: { type: "save" } };
      secondary = { text: "Cancel", intent: { type: "cancel" } };
    } else {
      const event = editorSubmitEvent(requiresQa);
      primary = {
        text: editorSubmitActionLabel(requiresQa, isEditorResubmit({ qaStatus, revisionCount })),
        intent: { type: "submit", event },
      };
    }
  }

  const backward = getBackwardStatusChange(status, { requiresQa, currentUserId });
  if (!secondary && backward) {
    secondary =
      backward.event === VideoProject.Event.REOPEN_EDITING
        ? { text: "Re-open", intent: { type: "reopen" } }
        : {
            text: statusMenuActionLabel(backward.event),
            intent: { type: "status_event", event: backward.event, userId: backward.userId },
          };
  }

  const menu: EditorPageActionSpec[] = [];
  menu.push({ text: "Duplicate", icon: "copy", intent: { type: "duplicate" } });
  menu.push({ text: "Archive", icon: "archive", intent: { type: "archive" } });

  return withViewQaStudio({ primary, secondary, menu }, { status, archived });
}
